import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { updateUserWinicoins } from '../../utils/winicoinsUtils';
import useAuthListener from '../../hooks/useAuthListener';
import { auth, db } from '../../firebase/firebase';
import { doc, getDoc } from 'firebase/firestore';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import wintoCoin from '../../assets/images/winto-coin.png';
import './InvestmentSuccess.css';

const InvestmentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [investmentDetails, setInvestmentDetails] = useState(null);
  const [winicoinsUpdated, setWinicoinsUpdated] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const { user, loading } = useAuthListener();
  const pdfRef = useRef(null);
  


  useEffect(() => {
    const amount = searchParams.get('amount');
    const duration = searchParams.get('duration');
    const userId = searchParams.get('userId');
    const winicoins = searchParams.get('winicoins');
    

    
    if (amount && duration) {
      const multiplier = getMultiplier(duration);
      const result = parseInt(amount) * multiplier;
      
      setInvestmentDetails({
        amount: parseInt(amount),
        duration: duration,
        multiplier: multiplier,
        result: result,
        planTitle: getPlanTitle(amount),
        winicoins: parseInt(winicoins) || 0
      });

      // Update winicoins if we have the data and not already updated
      if (amount && !winicoinsUpdated) {
        // Function to attempt winicoins update
        const attemptUpdate = () => {
          if (user && !winicoinsUpdated) {
            updateWinicoins(parseInt(amount));
          } else if (!loading && !user) {
            // Retry after 3 seconds if still not authenticated
            setTimeout(attemptUpdate, 3000);
          } else if (loading) {
            // Retry after 1 second if still loading
            setTimeout(attemptUpdate, 1000);
          }
        };

        // Start the update process
        attemptUpdate();
      }
    }
  }, [searchParams, user, winicoinsUpdated]);

  // Send email notification when investment details are loaded
  useEffect(() => {
    if (investmentDetails && user && !emailSent) {
      sendEmailNotification();
    }
  }, [investmentDetails, user, emailSent]);

  const updateWinicoins = async (amount) => {
    try {
      // Check if we have a user from auth state
      if (!user) {
        const userId = searchParams.get('userId');
        if (userId) {
          // We could implement a different approach here if needed
        }
        throw new Error('User authentication required to update winicoins');
      }
      
      const result = await updateUserWinicoins(amount);
      setWinicoinsUpdated(true);
    } catch (error) {
      console.error('Error updating winicoins:', error);
      setUpdateError(`Failed to update winicoins: ${error.message}. Please contact support.`);
    }
  };

  const getMultiplier = (duration) => {
    switch (duration) {
      case '6': return 4;
      case '12': return 8;
      case '24': return 12;
      default: return 8;
    }
  };

  const getPlanTitle = (amount) => {
    const amountNum = parseInt(amount);
    switch (amountNum) {
      case 72: return 'Basic';
      case 144: return 'Starter';
      case 360: return 'Growth';
      case 720: return 'Advanced';
      case 1440: return 'Professional';
      case 3600: return 'Enterprise';
      case 7200: return 'Premium';
      case 14400: return 'Ultimate';
      case 36000: return 'Elite';
      case 72000: return 'Royal';
      case 144000: return 'Empire';
      case 360000: return 'Legacy';
      default: return 'Custom';
    }
  };

  const handleBackToInvestments = () => {
    navigate('/buy-winis', { state: { scrollToPlans: true } });
  };

  const handleViewWallet = () => {
    navigate('/wallet');
  };

  const generatePDF = async () => {
    if (!pdfRef.current) return;
    
    setIsGeneratingPDF(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = `Investment_Success_${investmentDetails.planTitle}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const sendEmailNotification = async () => {
    if (!user || emailSent) return;
    
    try {
      // Get user email from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userEmail = userDoc.data()?.email;
      
      if (!userEmail) {
        console.error('User email not found');
        return;
      }

      const emailData = {
        to: userEmail,
        subject: `Investment Success - ${investmentDetails.planTitle} Plan`,
        message: `
          <h2>🎉 Investment Successful!</h2>
          <p>Dear Valued Investor,</p>
          
          <p>Congratulations! Your investment has been successfully processed. Here are the details of your investment:</p>
          
          <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3>Investment Details:</h3>
            <ul>
              <li><strong>Plan:</strong> ${investmentDetails.planTitle}</li>
              <li><strong>Amount Invested:</strong> $${investmentDetails.amount.toLocaleString()}</li>
              <li><strong>Duration:</strong> ${investmentDetails.duration === '6' ? '6 months' : investmentDetails.duration === '12' ? '1 year' : '2 years'}</li>
              <li><strong>Expected Return:</strong> $${investmentDetails.result.toLocaleString()}</li>
              <li><strong>Return Rate:</strong> ${(investmentDetails.multiplier * 100)}%</li>
              ${investmentDetails.winicoins > 0 ? `<li><strong>Winicoins Earned:</strong> ${investmentDetails.winicoins} WNC</li>` : ''}
            </ul>
          </div>
          
          <p>Your investment is now active and will be managed by our expert team. You can track your investment progress in your wallet.</p>
          
          <p>Thank you for choosing iWinto for your investment needs!</p>
          
          <p>Best regards,<br>The iWinto Team</p>
        `
      };

      // Send email via FormSubmit
      const response = await fetch('https://formsubmit.co/ajax/your-email@example.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        setEmailSent(true);
        console.log('Email notification sent successfully');
      } else {
        console.error('Failed to send email notification');
      }
    } catch (error) {
      console.error('Error sending email notification:', error);
    }
  };

  if (!investmentDetails) {
    return (
      <div className="investment-success-page">
        <div className="investment-success-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="investment-success-page">
      <div className="investment-success-container" ref={pdfRef}>
        {/* PDF Download Button */}
        <button 
          className="pdf-download-btn"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          title="Download as PDF"
        >
          {isGeneratingPDF ? (
            <span className="pdf-loading">⏳</span>
          ) : (
            <span className="pdf-icon">📄</span>
          )}
        </button>
        <div className="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22,4 12,14.01 9,11.01"></polyline>
          </svg>
        </div>
        
        <h1 className="investment-success-title">Payment Successful!</h1>
        <p className="success-subtitle">
          Your Payment has been processed successfully. Welcome to the {investmentDetails.planTitle} plan!
        </p>

        <div className="investment-summary">
          <div className="summary-item">
            <span className="summary-label"> Amount Bought:</span>
            <span className="summary-value">${investmentDetails.amount.toLocaleString()}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Duration:</span>
            <span className="summary-value">
              {investmentDetails.duration === '6' ? '6 months' : 
               investmentDetails.duration === '12' ? '1 year' : '2 years'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Expected Return:</span>
            <span className="summary-value return-value">
              ${investmentDetails.result.toLocaleString()}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Return Rate:</span>
            <span className="summary-value rate-value">
              {(investmentDetails.multiplier * 100)}%
            </span>
          </div>
          {investmentDetails.winicoins > 0 && (
            <div className="summary-item winicoins-item-investment">
              <span className="summary-label">Winicoins Earned:</span>
              <span className="summary-value winicoins-value">
                <img src={wintoCoin} alt="Winicoin" className="winicoin-icon" />
                {investmentDetails.winicoins} WNC
              </span>
            </div>
          )}
        </div>

        <div className="success-message">
          <p>
           Thank you for you purchase. Be ready to grow your money with us in the future.
          </p>
          

          
          {winicoinsUpdated && (
            <p className="winicoins-success">
              ✅ Your winicoins have been successfully added to your wallet!
            </p>
          )}
          {updateError && (
            <div className="winicoins-error">
              <p>⚠️ {updateError}</p>
              <button 
                className="retry-winicoins-btn"
                onClick={() => {
                  setUpdateError(null);
                  setWinicoinsUpdated(false);
                  if (investmentDetails) {
                    updateWinicoins(investmentDetails.amount);
                  }
                }}
              >
                Retry Winicoins Update
              </button>
            </div>
          )}
        </div>

        <div className="investment-success-actions">
          <button 
            className="btn-secondary"
            onClick={handleBackToInvestments}
          >
            Buy More Winis
          </button>
          <button 
            className="btn-primary"
            onClick={handleViewWallet}
          >
            Go to Wallet
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentSuccess;
